<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import axios from "axios";
import Swal from "sweetalert2";
import { LOCATION_TYPES, locationIdFromName } from "./companySettingsRules";
import { useCompanySettings } from "./useCompanySettings";

const { form, canEdit, setDefaultLocation } = useCompanySettings();

const locations = ref([]);
const loading = ref(false);
const busyId = ref("");
const modalOpen = ref(false);
const saving = ref(false);
const editingId = ref("");
const draft = reactive({ locationname: "", locationtype: "STORE", address: "", is_active: true });
const draftError = ref("");

const maxLocations = computed(() => Number(form.MaxLocations || 1));
const limitReached = computed(() => locations.value.length >= maxLocations.value);
const usagePercent = computed(() => Math.min(100, Math.round((locations.value.length * 100) / maxLocations.value)));
const defaultId = computed(() => form.DefaultLocationId || locations.value.find((l) => l.is_default)?.locationid || "");
const typeLabel = (value) => LOCATION_TYPES.find((type) => type.value === value)?.label || value;
const generatedId = computed(() =>
  locationIdFromName(draft.locationname, locations.value.map((l) => l.locationid))
);
const editingDefault = computed(() => !!editingId.value && editingId.value === defaultId.value);

const columns = [
  { title: "Location", key: "name" },
  { title: "Type", key: "type", width: 130 },
  { title: "Address", dataIndex: "address", key: "address", ellipsis: true },
  { title: "Active", key: "active", width: 90 },
  { title: "", key: "actions", width: 200, align: "right" },
];

const apiError = (error, fallback) => error?.response?.data?.detail || fallback;

async function loadLocations() {
  loading.value = true;
  try {
    const { data } = await axios.get("/v1/locations", { params: { include_inactive: true } });
    locations.value = data || [];
  } catch (error) {
    Swal.fire("Error", apiError(error, "Could not load locations."), "error");
  } finally {
    loading.value = false;
  }
}

function openCreate() {
  editingId.value = "";
  Object.assign(draft, { locationname: "", locationtype: "STORE", address: "", is_active: true });
  draftError.value = "";
  modalOpen.value = true;
}

function openEdit(location) {
  editingId.value = location.locationid;
  Object.assign(draft, {
    locationname: location.locationname,
    locationtype: location.locationtype || "STORE",
    address: location.address || "",
    is_active: !!location.is_active,
  });
  draftError.value = "";
  modalOpen.value = true;
}

async function saveDraft() {
  const name = draft.locationname.trim();
  if (!name) {
    draftError.value = "Location name is required";
    return;
  }
  const clash = locations.value.find(
    (l) => l.locationname.trim().toLowerCase() === name.toLowerCase() && l.locationid !== editingId.value
  );
  if (clash) {
    draftError.value = "Another location already has this name";
    return;
  }
  saving.value = true;
  try {
    const body = { locationname: name, locationtype: draft.locationtype, address: draft.address.trim() || null, is_active: draft.is_active };
    if (editingId.value) {
      await axios.put(`/v1/locations/${editingId.value}`, body);
    } else {
      await axios.post("/v1/locations", { ...body, locationid: generatedId.value, is_default: locations.value.length === 0 });
    }
    modalOpen.value = false;
    await loadLocations();
  } catch (error) {
    draftError.value = apiError(error, "Could not save the location.");
  } finally {
    saving.value = false;
  }
}

async function makeDefault(location) {
  const result = await Swal.fire({
    title: `Make "${location.locationname}" the default?`,
    text: "New users and bills start in the default location.",
    icon: "question",
    showCancelButton: true,
    confirmButtonText: "Make default",
  });
  if (!result.isConfirmed) return;
  busyId.value = location.locationid;
  try {
    await axios.put(`/v1/locations/${location.locationid}`, { is_default: true, is_active: true });
    await setDefaultLocation(location.locationid);
    await loadLocations();
  } catch (error) {
    Swal.fire("Error", apiError(error, "Could not change the default location."), "error");
  } finally {
    busyId.value = "";
  }
}

async function toggleActive(location, active) {
  if (!active && location.locationid === defaultId.value) {
    Swal.fire("Default location", "Make another location the default before turning this one off.", "info");
    return;
  }
  busyId.value = location.locationid;
  try {
    await axios.put(`/v1/locations/${location.locationid}`, { is_active: active });
    location.is_active = active;
  } catch (error) {
    Swal.fire("Error", apiError(error, "Could not update the location."), "error");
  } finally {
    busyId.value = "";
  }
}

onMounted(loadLocations);
</script>

<template>
  <div class="cs-panel">
    <section class="cs-group">
      <header class="cs-group-head d-flex flex-wrap justify-content-between align-items-start gap-2">
        <div>
          <h5>Locations</h5>
          <p>Stores, warehouses and counters. Stock is kept per location.</p>
        </div>
        <a-tooltip :title="limitReached ? `Your plan allows ${maxLocations} location(s).` : ''">
          <button v-if="canEdit" type="button" class="btn btn-primary btn-sm" :disabled="limitReached" @click="openCreate">
            <vue-feather type="plus" size="14"></vue-feather> Add location
          </button>
        </a-tooltip>
      </header>

      <div class="cs-usage">
        <span>{{ locations.length }} of {{ maxLocations }} location{{ maxLocations === 1 ? "" : "s" }} used</span>
        <a-progress :percent="usagePercent" :show-info="false" size="small" status="normal" stroke-color="#fb923c" />
      </div>

      <a-table
        :columns="columns"
        :data-source="locations"
        :loading="loading"
        :pagination="false"
        row-key="locationid"
        size="middle"
        :scroll="{ x: 640 }"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'name'">
            <div class="fw-semibold">
              {{ record.locationname }}
              <a-tag v-if="record.locationid === defaultId" color="orange" class="ms-1">Default</a-tag>
            </div>
            <small class="text-muted">ID: {{ record.locationid }}</small>
          </template>
          <template v-else-if="column.key === 'type'">{{ typeLabel(record.locationtype) }}</template>
          <template v-else-if="column.key === 'active'">
            <a-switch
              :checked="record.is_active"
              :loading="busyId === record.locationid"
              :disabled="!canEdit"
              size="small"
              @change="(value) => toggleActive(record, value)"
            />
          </template>
          <template v-else-if="column.key === 'actions' && canEdit">
            <button
              v-if="record.locationid !== defaultId"
              type="button"
              class="btn btn-link btn-sm"
              :disabled="busyId === record.locationid"
              @click="makeDefault(record)"
            >
              Make default
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="openEdit(record)">Edit</button>
          </template>
        </template>
        <template #emptyText>
          <div class="py-4 text-muted">No locations yet. Add your first store or warehouse.</div>
        </template>
      </a-table>
    </section>

    <a-modal
      v-model:visible="modalOpen"
      :title="editingId ? 'Edit location' : 'Add location'"
      :ok-text="editingId ? 'Save' : 'Add location'"
      :confirm-loading="saving"
      :mask-closable="!saving"
      :z-index="1100"
      wrap-class-name="cs-modal"
      @ok="saveDraft"
    >
      <div class="cs-field">
        <label class="cs-label" for="cs-loc-name">Name <span class="cs-required">*</span></label>
        <input
          id="cs-loc-name"
          v-model="draft.locationname"
          class="form-control"
          maxlength="100"
          placeholder="e.g. Main Store"
          @keyup.enter="saveDraft"
        />
        <div v-if="!editingId && draft.locationname.trim()" class="cs-hint">ID will be <code>{{ generatedId }}</code></div>
      </div>
      <div class="cs-field">
        <label class="cs-label">Type</label>
        <a-radio-group v-model:value="draft.locationtype" button-style="solid">
          <a-radio-button v-for="type in LOCATION_TYPES" :key="type.value" :value="type.value">{{ type.label }}</a-radio-button>
        </a-radio-group>
      </div>
      <div class="cs-field">
        <label class="cs-label" for="cs-loc-address">Address <span class="cs-optional">optional</span></label>
        <textarea id="cs-loc-address" v-model="draft.address" class="form-control" rows="2" maxlength="250"></textarea>
      </div>
      <div v-if="editingId" class="cs-field d-flex align-items-center gap-2">
        <a-switch v-model:checked="draft.is_active" :disabled="editingDefault" />
        <span>Active</span>
        <small v-if="editingDefault" class="text-muted">(the default location is always active)</small>
      </div>
      <div v-if="draftError" class="cs-error" role="alert">{{ draftError }}</div>
    </a-modal>
  </div>
</template>
